import { IHierarchyVm } from '@aon/tws-common-ui-hierarchy';
import _ from 'lodash';
import { Reducer } from 'redux';
import { TThunkAction } from '.';
import { AudienceSourceType, IHierarchyMetric } from '../models';
import * as HierarchyAPI from '../services/HierarchyAPI';
import {
	IHierarchyGetHierarchyMetricsSucceeded,
	IHierarchyGetHierarchyVmFailed,
	IHierarchyGetHierarchyVmRequested,
	IHierarchyGetHierarchyVmSucceeded,
	IHierarchyResetState,
} from './Actions';
import { ActionTypes } from './ActionTypes';
import { ActionCreators as DistributionHierarchyActionCreators } from './DistributionHierarchyStore';

export type TCommonHierarchyState =
	// Loading data. Prior hierarchy info fetched will still be available
	| ({ isLoading: true } & Partial<IHierarchyVm>)
	// The request has been successfully resolved - data is available
	| (IHierarchyVm & { couldNotFetchData?: false })
	// The request could not be resolved successfully - data is not available
	| { isLoading: false; couldNotFetchData: true };

export const defaultState: Readonly<TCommonHierarchyState> = {};

export type TActionCreator<R> = TThunkAction<R, KnownActions>;

export const ActionCreators = {
	GetHierarchyVm:
		(hierarchyMetadataId: string, includeOwners: boolean, audienceSourceType?: AudienceSourceType): TActionCreator<void> =>
		async (dispatch) => {
			dispatch({ type: ActionTypes.HIERARCHY_GET_HIERARCHY_VM_REQUESTED });

			try {
				const hierarchy = await HierarchyAPI.getHierarchy({ hierarchyId: hierarchyMetadataId, includeOwners });
				dispatch({ type: ActionTypes.HIERARCHY_GET_HIERARCHY_VM_SUCCEEDED, payload: hierarchy });

				if (audienceSourceType === AudienceSourceType.Hierarchy) {
					const hierarchyMetrics = await HierarchyAPI.getHierarchyMetrics(hierarchyMetadataId, hierarchy.rootLevelNodes ?? []);
					dispatch({ type: ActionTypes.HIERARCHY_GET_HIERARCHY_METRICS_SUCCEEDED, payload: hierarchyMetrics });

					dispatch(DistributionHierarchyActionCreators.AutoSelectAll() as any);
				}
			} catch {
				dispatch({ type: ActionTypes.HIERARCHY_GET_HIERARCHY_VM_FAILED });
			}
		},
	ResetHierarchyState: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.HIERARCHY_RESET_STATE });
	},
};

export type KnownActions =
	| IHierarchyGetHierarchyVmRequested
	| IHierarchyGetHierarchyVmSucceeded
	| IHierarchyGetHierarchyVmFailed
	| IHierarchyGetHierarchyMetricsSucceeded
	| IHierarchyResetState;

export const reducer: Reducer<TCommonHierarchyState, KnownActions> = (
	state: TCommonHierarchyState | undefined,
	action: KnownActions,
): TCommonHierarchyState => {
	if (state === undefined) {
		return reducer(defaultState, action);
	}

	switch (action.type) {
		case ActionTypes.HIERARCHY_GET_HIERARCHY_VM_REQUESTED: {
			if (!state.isLoading && state.couldNotFetchData) {
				return { isLoading: true };
			}

			return { ...state, isLoading: true };
		}
		case ActionTypes.HIERARCHY_GET_HIERARCHY_VM_SUCCEEDED: {
			const hierarchyVm: IHierarchyVm = action.payload;
			hierarchyVm.isLoading = false;

			// Update parentIds
			hierarchyVm.rootLevelNodes?.forEach((rootNodeId) => {
				const rootNodeVm = hierarchyVm.nodes?.[rootNodeId];

				rootNodeVm?.childrenIds?.forEach((childNodeId) => {
					(hierarchyVm.parentIds ??= {})[childNodeId] = rootNodeId;
				});
			});

			return hierarchyVm;
		}
		case ActionTypes.HIERARCHY_GET_HIERARCHY_VM_FAILED: {
			return { isLoading: false, couldNotFetchData: true };
		}
		case ActionTypes.HIERARCHY_GET_HIERARCHY_METRICS_SUCCEEDED: {
			if (state.isLoading || state.couldNotFetchData) {
				return state;
			}

			const hierarchyVm = _.cloneDeep(state);
			const hierarchyMetrics = action.payload;

			Object.keys(hierarchyMetrics ?? {}).forEach((nodeId) => {
				const node = hierarchyVm.nodes?.[nodeId];
				const totalRollups = _.find(hierarchyMetrics[nodeId].detailMetrics, (x: IHierarchyMetric) => x.name === 'total-rollup')?.value;

				if (node && totalRollups) {
					node.totalRollups = totalRollups + 1;
				}
			});

			return hierarchyVm;
		}
		case ActionTypes.HIERARCHY_RESET_STATE: {
			return { ...defaultState };
		}
		default:
			return state;
	}
};
